import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, ChevronDown, Dices, ExternalLink, Search, Share2, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import './GrowthPanels.css';

const RETI_STAKE_URL = 'https://reti.nodely.io/validators/19';
const SITE_URL = 'https://poorslugspool.xyz';
const SWAP_URL = process.env.REACT_APP_SWAP_URL || '/swap';
const X_HANDLE = 'Slugggorand';
const X_PROFILE_URL = `https://x.com/${X_HANDLE}`;
const NFD_API_URL = 'https://api.nf.domains/nfd';
const WALLET_STORAGE_KEY = 'poorslugspool:wallet';
const ALGORAND_ADDRESS_PATTERN = /^[A-Z2-7]{58}$/;
const STAKE_PRESETS = [1000, 5000, 10000, 50000];

const toNumber = (value) => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

const formatAlgo = (value, digits = 2) => {
  const numeric = toNumber(value) ?? 0;
  if (Math.abs(numeric) >= 1_000_000) return `${(numeric / 1_000_000).toFixed(2)}M`;
  if (Math.abs(numeric) >= 10_000) return `${(numeric / 1_000).toFixed(1)}K`;
  return numeric.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatDuration = (ms) => {
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

const readStoredWallet = () => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('wallet');
    if (fromUrl) return fromUrl;
    return window.localStorage.getItem(WALLET_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const storeWallet = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(WALLET_STORAGE_KEY, value);
    } else {
      window.localStorage.removeItem(WALLET_STORAGE_KEY);
    }
  } catch {
    // Storage can be unavailable (private mode); the lookup still works for this visit.
  }
};

const resolveWalletInput = async (input) => {
  const trimmed = input.trim();
  const upper = trimmed.toUpperCase();
  if (ALGORAND_ADDRESS_PATTERN.test(upper)) {
    return { address: upper, label: null };
  }

  const name = trimmed.toLowerCase().endsWith('.algo') ? trimmed.toLowerCase() : `${trimmed.toLowerCase()}.algo`;
  const response = await fetch(`${NFD_API_URL}/${encodeURIComponent(name)}?view=brief`);
  if (!response.ok) {
    throw new Error(`Couldn't find ${name}. Try pasting your wallet address instead.`);
  }
  const nfd = await response.json();
  const address = nfd.depositAccount || nfd.owner;
  if (!address) {
    throw new Error(`${name} isn't linked to a wallet yet.`);
  }
  return { address, label: name };
};

function JoinPoolHero({ poolData, formatCurrency, algoPrice }) {
  const [stakeInput, setStakeInput] = useState('10000');
  const [shareState, setShareState] = useState('idle');

  const performance = poolData?.performance ?? {};
  const netApy = toNumber(performance.estimated_apy_30d) ?? toNumber(performance.estimated_apy) ?? 0;
  const totalStaked = toNumber(poolData?.total_staked_algo) ?? 0;
  const stakerCount = poolData?.stakers?.length ?? 0;
  const totalBlocks = toNumber(performance.total_blocks) ?? 0;
  const minEntry = (toNumber(poolData?.pool_info?.global_state?.minEntryStake) ?? 1_000_000_000) / 1_000_000;

  const stakeAmount = Math.max(0, toNumber(stakeInput) ?? 0);
  const yearly = stakeAmount * (netApy / 100);
  const projections = [
    { label: 'Per day', value: yearly / 365 },
    { label: 'Per month', value: yearly / 12 },
    { label: 'Per year', value: yearly },
  ];
  const belowMinimum = stakeAmount > 0 && stakeAmount < minEntry;

  const handleShare = async () => {
    const text = `Staking my ALGO with PoorSlugsPool: ${netApy.toFixed(2)}% APY, ${totalBlocks.toLocaleString('en-US')} blocks proposed this year. Live stats:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'PoorSlugsPool', text, url: SITE_URL });
        return;
      }
      await navigator.clipboard.writeText(`${text} ${SITE_URL}`);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 2000);
    } catch {
      // Share sheet dismissed or clipboard blocked; nothing to do.
    }
  };

  const tweetUrl = `https://x.com/intent/post?text=${encodeURIComponent(
    `Earning ${netApy.toFixed(2)}% APY staking ALGO with PoorSlugsPool by @${X_HANDLE} 🐌 Live pool stats:`,
  )}&url=${encodeURIComponent(SITE_URL)}`;

  return (
    <section className="gp-hero" aria-labelledby="gp-hero-title">
      <div className="gp-hero-copy">
        <span className="gp-eyebrow"><Sparkles size={14} /> Reti Validator #19 · Open to new stakers</span>
        <h2 id="gp-hero-title" className="gp-hero-title">
          Put your ALGO to work at <span className="gp-gradient-text">{netApy.toFixed(2)}% APY</span>
        </h2>
        <p className="gp-hero-sub">
          Stake through the Reti protocol and earn a share of every block this node proposes.
          Your ALGO stays in the Reti smart contract, never with the operator, and rewards compound into your stake automatically.
        </p>

        <div className="gp-hero-stats">
          <div><strong>{formatAlgo(totalStaked, 0)}</strong><span>ALGO staked</span></div>
          <div><strong>{stakerCount}</strong><span>stakers</span></div>
          <div><strong>{totalBlocks.toLocaleString('en-US')}</strong><span>blocks in the last year</span></div>
          <div><strong>1%</strong><span>commission</span></div>
        </div>

        <div className="gp-cta-row">
          <a className="gp-btn gp-btn-primary" href={RETI_STAKE_URL} target="_blank" rel="noopener noreferrer">
            Stake now on Reti <ArrowRight size={16} />
          </a>
          <button type="button" className="gp-btn gp-btn-ghost" onClick={handleShare}>
            {shareState === 'copied' ? <><Check size={16} /> Link copied</> : <><Share2 size={16} /> Share</>}
          </button>
          <a className="gp-btn gp-btn-ghost" href={tweetUrl} target="_blank" rel="noopener noreferrer">
            Post on X
          </a>
        </div>

        <ol className="gp-steps">
          <li><span>1</span>Get a wallet like Pera or Defly holding at least {minEntry.toLocaleString('en-US')} ALGO</li>
          <li><span>2</span>Open Reti and connect your wallet</li>
          <li><span>3</span>Add stake to Validator #19, and you're earning from the next block</li>
        </ol>
      </div>

      <div className="gp-estimator" aria-label="Earnings estimate">
        <div className="gp-estimator-head">
          <Wallet size={18} />
          <span>What would I earn?</span>
        </div>
        <label className="gp-input-label" htmlFor="gp-stake-input">Stake amount (ALGO)</label>
        <input
          id="gp-stake-input"
          className="gp-input"
          type="number"
          inputMode="decimal"
          min="0"
          value={stakeInput}
          onChange={(event) => setStakeInput(event.target.value)}
        />
        <div className="gp-presets">
          {STAKE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={`gp-chip${stakeAmount === preset ? ' is-active' : ''}`}
              onClick={() => setStakeInput(String(preset))}
            >
              {preset / 1000}K
            </button>
          ))}
        </div>
        <div className="gp-projections">
          {projections.map(({ label, value }) => (
            <div key={label} className="gp-projection">
              <span>{label}</span>
              <strong>{formatAlgo(value, value < 10 ? 2 : 1)} ALGO</strong>
              <em>{formatCurrency(value * algoPrice)}</em>
            </div>
          ))}
        </div>
        {belowMinimum ? (
          <p className="gp-note gp-note-warn">The pool's minimum stake is {minEntry.toLocaleString('en-US')} ALGO.</p>
        ) : (
          <p className="gp-note">Based on the last 30 days, after the 1% commission. Block rewards vary day to day.</p>
        )}
      </div>
    </section>
  );
}

function WalletLookup({ poolData, formatCurrency, algoPrice }) {
  const [input, setInput] = useState(readStoredWallet);
  const [resolved, setResolved] = useState(null);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const stakers = useMemo(() => {
    const rows = Array.isArray(poolData?.staker_rewards) ? poolData.staker_rewards : [];
    return [...rows].sort((a, b) => (b.staked_amount_algo ?? 0) - (a.staked_amount_algo ?? 0));
  }, [poolData]);

  const lookup = async (value) => {
    const trimmed = (value ?? input).trim();
    if (!trimmed) return;
    setStatus('loading');
    setMessage('');
    try {
      const result = await resolveWalletInput(trimmed);
      setResolved(result);
      setStatus('done');
      storeWallet(trimmed);
    } catch (error) {
      setResolved(null);
      setStatus('error');
      setMessage(error.message || 'Lookup failed. Please try again.');
    }
  };

  // Re-run a remembered wallet once pool data is available.
  useEffect(() => {
    if (input && stakers.length && status === 'idle') {
      lookup(input);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakers.length]);

  const clear = () => {
    setInput('');
    setResolved(null);
    setStatus('idle');
    setMessage('');
    storeWallet('');
  };

  const rankIndex = resolved ? stakers.findIndex((row) => row.address === resolved.address) : -1;
  const staker = rankIndex >= 0 ? stakers[rankIndex] : null;
  const shortAddress = resolved ? `${resolved.address.slice(0, 6)}…${resolved.address.slice(-4)}` : '';

  const metric = (algoValue) => (
    <>
      <strong>{formatAlgo(algoValue)} ALGO</strong>
      <em>{formatCurrency((toNumber(algoValue) ?? 0) * algoPrice)}</em>
    </>
  );

  return (
    <section className="gp-card" aria-labelledby="gp-lookup-title">
      <div className="gp-card-head">
        <span className="gp-card-icon gp-blue"><Search size={18} /></span>
        <div>
          <h3 id="gp-lookup-title">Check my wallet</h3>
          <p>Paste your address or .algo name to see your stake and earnings.</p>
        </div>
      </div>

      <form
        className="gp-lookup-form"
        onSubmit={(event) => {
          event.preventDefault();
          lookup();
        }}
      >
        <input
          className="gp-input"
          placeholder="ABCD…XYZ or yourname.algo"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-label="Wallet address or NFD name"
        />
        <button type="submit" className="gp-btn gp-btn-primary" disabled={status === 'loading'}>
          {status === 'loading' ? 'Checking…' : 'Check'}
        </button>
      </form>

      {status === 'error' && <p className="gp-note gp-note-warn">{message}</p>}

      {status === 'done' && staker && (
        <div className="gp-wallet-result">
          <div className="gp-wallet-title">
            <span className="gp-pill gp-pill-green">Staking</span>
            <span>{resolved.label || shortAddress}</span>
            <span className="gp-rank">Rank #{rankIndex + 1} of {stakers.length}</span>
          </div>
          <div className="gp-wallet-grid">
            <div><span>Your stake</span>{metric(staker.staked_amount_algo)}</div>
            <div><span>Pool share</span><strong>{formatAlgo(staker.stake_percentage)}%</strong><em>of {formatAlgo(poolData?.total_staked_algo, 0)} ALGO</em></div>
            <div><span>Earned so far</span>{metric(staker.total_earned_algo)}</div>
            <div><span>Est. per day</span>{metric(staker.estimated_rewards_per_day_algo ?? staker.daily_rewards_algo)}</div>
            <div><span>Est. per month</span>{metric(staker.estimated_rewards_30d_algo)}</div>
            <div><span>Est. per year</span>{metric(staker.estimated_rewards_1y_algo)}</div>
          </div>
          <button type="button" className="gp-link-btn" onClick={clear}>Use a different wallet</button>
        </div>
      )}

      {status === 'done' && !staker && (
        <div className="gp-wallet-result gp-wallet-empty">
          <p><strong>{resolved.label || shortAddress}</strong> isn't staking in this pool yet.</p>
          <a className="gp-btn gp-btn-primary" href={RETI_STAKE_URL} target="_blank" rel="noopener noreferrer">
            Start staking <ExternalLink size={14} />
          </a>
          <button type="button" className="gp-link-btn" onClick={clear}>Try another wallet</button>
        </div>
      )}

      {status !== 'done' && status !== 'error' && (
        <p className="gp-note"><ShieldCheck size={13} /> Read-only. Your wallet is remembered on this device only.</p>
      )}
    </section>
  );
}

function BlockLuckMeter({ poolData, lastBlockTime }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const performance = poolData?.performance ?? {};
  const pace7d = toNumber(performance.avg_blocks_per_day_7d);
  const pace30d = toNumber(performance.avg_blocks_per_day_30d);
  const blocksPerDay = pace30d || pace7d || toNumber(performance.avg_blocks_per_day) || 0;

  if (!blocksPerDay) return null;

  const luck = pace7d && pace30d ? ((pace7d / pace30d) - 1) * 100 : 0;
  const luckClamped = Math.max(-50, Math.min(50, luck));
  const needle = 50 + luckClamped; // 0..100 across the gauge

  const intervalMs = (24 * 60 * 60 * 1000) / blocksPerDay;
  const nextHourChance = (1 - Math.exp(-(60 * 60 * 1000) / intervalMs)) * 100;
  const hasLastBlock = lastBlockTime instanceof Date && !Number.isNaN(lastBlockTime.getTime());
  const sinceMs = hasLastBlock ? Math.max(0, now - lastBlockTime.getTime()) : null;

  let mood = { label: 'Right on pace', tone: 'gp-blue' };
  if (luck >= 8) mood = { label: 'Hot streak', tone: 'gp-green' };
  else if (luck <= -8) mood = { label: 'Cooling off', tone: 'gp-orange' };

  return (
    <section className="gp-card" aria-labelledby="gp-luck-title">
      <div className="gp-card-head">
        <span className="gp-card-icon gp-purple"><Dices size={18} /></span>
        <div>
          <h3 id="gp-luck-title">Block luck</h3>
          <p>This week's block pace compared with the 30-day average.</p>
        </div>
        <span className={`gp-pill ${mood.tone}`}>{mood.label}</span>
      </div>

      <div className="gp-gauge" role="img" aria-label={`Block luck ${luck >= 0 ? '+' : ''}${luck.toFixed(0)}% versus 30-day average`}>
        <div className="gp-gauge-track" />
        <div className="gp-gauge-needle" style={{ left: `${needle}%` }} />
        <div className="gp-gauge-labels"><span>Unlucky</span><span>Average</span><span>Lucky</span></div>
      </div>

      <div className="gp-luck-grid">
        <div>
          <span>vs 30D pace</span>
          <strong className={luck >= 0 ? 'gp-text-green' : 'gp-text-orange'}>{luck >= 0 ? '+' : ''}{luck.toFixed(1)}%</strong>
          <em>{pace7d?.toFixed(1) ?? '—'} vs {pace30d?.toFixed(1) ?? '—'} blocks/day</em>
        </div>
        <div>
          <span>Typical gap</span>
          <strong>{formatDuration(intervalMs)}</strong>
          <em>between blocks</em>
        </div>
        <div>
          <span>Next-hour odds</span>
          <strong>{nextHourChance.toFixed(0)}%</strong>
          <em>{sinceMs !== null ? `last block ${formatDuration(sinceMs)} ago` : 'chance of a block'}</em>
        </div>
      </div>
      <p className="gp-note">Blocks are random and weighted by stake, so streaks and dry spells are normal and even out over time.</p>
    </section>
  );
}

export default function GrowthPanels({ poolData, lastBlockTime, formatCurrency, algoPrice }) {
  if (!poolData) return null;
  return (
    <div className="gp-root">
      <JoinPoolHero poolData={poolData} formatCurrency={formatCurrency} algoPrice={algoPrice} />
      <div className="gp-grid">
        <WalletLookup poolData={poolData} formatCurrency={formatCurrency} algoPrice={algoPrice} />
        <BlockLuckMeter poolData={poolData} lastBlockTime={lastBlockTime} />
      </div>
    </div>
  );
}

const buildFaqItems = (minEntry) => [
  {
    q: 'Is my ALGO safe? Who holds it?',
    a: (
      <>
        Réti is non-custodial. Your stake sits in an on-chain pool smart contract created by the Réti protocol,
        not in a wallet the operator controls. The operator runs the node and can take it online or offline, but
        can't move stakers' ALGO or raise the commission. As with any smart contract there's still some risk; Réti's
        contracts are open source and their audits are published in the{' '}
        <a href="https://github.com/algorandfoundation/reti" target="_blank" rel="noopener noreferrer">Algorand Foundation repo</a>.
      </>
    ),
  },
  {
    q: 'Can I withdraw anytime?',
    a: (
      <>
        Yes. There's no lockup or waiting period. You can remove everything, or part of your stake as long as what's
        left stays above the {minEntry.toLocaleString('en-US')} ALGO minimum. One catch: removing stake partway
        through an epoch gives up the rewards earned so far in that epoch.
      </>
    ),
  },
  {
    q: 'How do rewards work? Do I need to claim them?',
    a: (
      <>
        No claiming. Each time this node proposes a block, the reward goes to the pool. At the end of every epoch
        the rewards are split by how much each staker has in and for how long, then added straight to your staked
        balance, so they compound automatically.
      </>
    ),
  },
  {
    q: "What's the commission?",
    a: (
      <>
        1% of block rewards. Réti fixes the commission when a pool is created, so it can't be raised later. Every
        APY on this site is shown after the commission.
      </>
    ),
  },
  {
    q: 'How much do I need to start?',
    a: (
      <>
        {minEntry.toLocaleString('en-US')} ALGO, plus a few extra ALGO for network fees. Staking solo on Algorand
        needs 30,000 ALGO and a node running 24/7 to earn rewards; a pool lets you earn with much less and no
        hardware.
      </>
    ),
  },
  {
    q: 'What if the node goes offline?',
    a: (
      <>
        Algorand doesn't slash staked ALGO, so downtime doesn't cost you principal. It would only mean missed rewards
        until the node is back. You can watch uptime and every block live on this page.
      </>
    ),
  },
  {
    q: 'Why does the APY change day to day?',
    a: (
      <>
        Block proposers are picked at random, weighted by stake, so some days bring more blocks than others. That's
        why this site shows 7-day, 30-day and 1-year figures, and the Block luck meter shows whether we're running
        hot or cold. Over time it evens out.
      </>
    ),
  },
  {
    q: 'Who runs PoorSlugsPool?',
    a: (
      <>
        It's run by <a href={X_PROFILE_URL} target="_blank" rel="noopener noreferrer">@{X_HANDLE}</a>, a member of
        the Algorand community enrolled in xGov. Questions are always welcome on X.
      </>
    ),
  },
];

export function SwapPopup({ onClose }) {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-content gp-swap-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gp-swap-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="popup-header gp-swap-header">
          <h2 id="gp-swap-title" className="popup-title">Slug Swap</h2>
          <a className="gp-swap-full" href={`${SWAP_URL}/`} target="_blank" rel="noopener noreferrer">
            Open full page
          </a>
          <button type="button" className="popup-close-btn" onClick={onClose} aria-label="Close swap">
            &times;
          </button>
        </div>
        <iframe
          className="gp-swap-frame"
          title="Slug Swap"
          src={`${SWAP_URL}/?embed=1`}
          allow="clipboard-write"
        />
      </div>
    </div>
  );
}

export function FaqPopup({ poolData, onClose }) {
  const [openIndex, setOpenIndex] = useState(0);
  const minEntry = (toNumber(poolData?.pool_info?.global_state?.minEntryStake) ?? 1_000_000_000) / 1_000_000;
  const items = buildFaqItems(minEntry);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup-content gp-faq-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gp-faq-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="popup-header">
          <h2 id="gp-faq-title" className="popup-title">Questions before you stake</h2>
          <button type="button" className="popup-close-btn" onClick={onClose} aria-label="Close FAQ">
            &times;
          </button>
        </div>

        <div className="popup-body">
          <div className="gp-faq-list">
            {items.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div key={item.q} className={`gp-faq-item${isOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="gp-faq-question"
                    aria-expanded={isOpen}
                    aria-controls={`gp-faq-answer-${index}`}
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  >
                    <span>{item.q}</span>
                    <ChevronDown size={18} aria-hidden="true" />
                  </button>
                  {isOpen && (
                    <div id={`gp-faq-answer-${index}`} className="gp-faq-answer">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="gp-operator">
            <div>
              <strong>Run by @{X_HANDLE}</strong>
              <span>Reti Validator #19 · 1% commission · Enrolled in xGov</span>
            </div>
            <a className="gp-btn gp-btn-ghost" href={X_PROFILE_URL} target="_blank" rel="noopener noreferrer">
              Follow on X
            </a>
          </div>
          <a className="gp-btn gp-btn-primary gp-faq-cta" href={RETI_STAKE_URL} target="_blank" rel="noopener noreferrer">
            Stake now on Reti <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}
